import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { config } from '../../config';
import { AuthService, User, UserRole } from '../../auth/auth.service';
import { ErrorService } from '../../shared/services/error.service';
import { ConversationService } from '../../services/conversation.service';
import { WebSocketService } from '../../auth/web-socket.service';
import { RCDataSyncService } from '../../services/rc-data-sync.service';
import { MCDataSyncService } from '../../services/mc-data-sync.service';
import { Conversation } from '../../models/conversation.model';
import { ContactStageService, ContactStage } from '../../shared/services/contact-stage.service';
import { RCOversightService, CompanyDropdownOption } from '../../services/rc-oversight.service';
import { GodService } from '../../services/god.service';
import { PermissionService } from '../../shared/services/permission.service';
import { CVUploadComponent } from '../../shared/components/cv-upload/cv-upload.component';
import { CVViewerComponent } from '../../shared/components/cv-viewer/cv-viewer.component';
import { TemplateQuickAccessComponent } from '../../conversations/template-quick-access/template-quick-access.component';
import { CommunicationTemplate } from '../../services/communication-template.service';

interface Contact {
  id?: string;
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  company_id: string;
  notes?: string;
  status?: string;
  job_application_id?: string;
  job_title?: string;
  stage?: {
    _id: string;
    name: string;
  };
  stage_updated_at?: string;
  stage_updated_by?: {
    _id: string;
    first_name: string;
    last_name: string;
  };
  company?: {
    id: string;
    name: string;
  };
  created_at?: string;
  updated_at?: string;
  cv_file_path?: string;
  cv_file_name?: string;
  cv_file_size?: number;
  cv_uploaded_at?: string;
}

interface CVFile {
  name: string;
  size: number;
  uploadedAt: Date;
  uploadedBy?: string;
}

interface Channel {
  id: string;
  name: string;
  company_id: string;
  channel_type: string;
  status: string;
  mobile?: string;
  email?: string;
}

@Component({
  selector: 'app-contacts',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, CVUploadComponent, CVViewerComponent, TemplateQuickAccessComponent],
  template: `
    <div class="contacts">
      <div class="contacts-header">
        <h2>Contacts</h2>
        <div class="header-controls">
          <div class="search-container">
            <label for="contactSearch">Search:</label>
            <div class="search-input-wrapper">
              <input 
                type="text" 
                id="contactSearch"
                [(ngModel)]="searchQuery"
                (input)="onSearchChange()"
                placeholder="Search by name, email, or stage..."
                class="search-input">
              <button 
                *ngIf="searchQuery" 
                class="clear-search-btn"
                (click)="clearSearch()"
                aria-label="Clear search">
                ✕
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Stage Overview Section -->
      <div class="stage-overview" *ngIf="!loading && availableStages.length > 0">
        <h3>Stage Overview</h3>
        <div class="stage-stats">
          <div 
            *ngFor="let stage of availableStages" 
            class="stage-stat"
            [class.active]="selectedStage === stage._id"
            (click)="selectStageFromOverview(stage._id)">
            <span class="stage-name">{{ stage.name }}:</span>
            <span class="stage-count">{{ getStageContactCount(stage._id) }}</span>
          </div>
          <div 
            class="stage-stat total"
            [class.active]="selectedStage === 'all'"
            (click)="selectStageFromOverview('all')">
            <span class="stage-name">Total:</span>
            <span class="stage-count">{{ getTotalContactCount() }}</span>
          </div>
        </div>
      </div>
      
      <div class="action-bar">
        <div class="left-actions">
          <button class="add-button" (click)="showAddContactForm()">Add New Contact</button>
          <button class="refresh-button" (click)="refreshData()" [disabled]="loading">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            Refresh
          </button>
        </div>
      </div>
      
      <div *ngIf="loading" class="loading">
        <span class="ap-spinner"></span>
        Loading contacts...
      </div>
      
      <div *ngIf="error" class="error">
        {{ error }}
        <button (click)="refreshData()">Try Again</button>
      </div>
      
      <div *ngIf="showForm" class="contact-form">
        <h3>{{ editingContact ? 'Edit Contact' : 'Add New Contact' }}</h3>
        <form [formGroup]="contactForm" (ngSubmit)="saveContact()">
          <div class="form-group">
            <label for="first_name">First Name <span class="required">*</span></label>
            <input type="text" id="first_name" formControlName="first_name" placeholder="Enter first name">
            <div *ngIf="contactForm.get('first_name')?.invalid && contactForm.get('first_name')?.touched" class="error-message">
              First name is required
            </div>
          </div>
          
          <div class="form-group">
            <label for="last_name">Last Name <span class="required">*</span></label>
            <input type="text" id="last_name" formControlName="last_name" placeholder="Enter last name">
            <div *ngIf="contactForm.get('last_name')?.invalid && contactForm.get('last_name')?.touched" class="error-message">
              Last name is required
            </div>
          </div>
          
          <div class="form-group">
            <label for="email">Email <span class="required">*</span></label>
            <input type="email" id="email" formControlName="email" placeholder="Enter email address">
            <div *ngIf="contactForm.get('email')?.invalid && contactForm.get('email')?.touched" class="error-message">
              <div *ngIf="contactForm.get('email')?.errors?.['required']">Email is required</div>
              <div *ngIf="contactForm.get('email')?.errors?.['email']">Please enter a valid email address</div>
            </div>
          </div>
          
          <div class="form-group">
            <label for="mobile">Mobile Number <span class="required">*</span></label>
            <input type="tel" id="mobile" formControlName="mobile" placeholder="+441234567890">
            <div *ngIf="contactForm.get('mobile')?.invalid && contactForm.get('mobile')?.touched" class="error-message">
              <span *ngIf="contactForm.get('mobile')?.errors?.['required']">Mobile number is required</span>
              <span *ngIf="contactForm.get('mobile')?.errors?.['pattern']">Use international format with country code, no spaces (e.g. +441234567890)</span>
            </div>
          </div>

          <div class="form-group">
            <label for="job_title">Job Title <span class="optional">(Optional)</span></label>
            <input type="text" id="job_title" formControlName="job_title" placeholder="Enter job title">
          </div>
          
          <!-- CV Upload Section - positioned before Notes -->
          <app-cv-upload #cvUpload
            [contactId]="editingContact?.id || null"
            [existingCvFile]="existingCvFile"
            [required]="false"
            (cvUploaded)="onCVUploaded($event)"
            (cvDeleted)="onCVDeleted()"
            (fileSelected)="onCVFileSelected($event)"
            (validationChange)="onCVValidationChange($event)">
          </app-cv-upload>

          <!-- Company Selection for God users only (RC users should not see MC company options) -->
          <div class="form-group" *ngIf="isGodUser()">
            <label for="mc_company_id">MC Company *</label>
            <select id="mc_company_id" formControlName="mc_company_id">
              <option value="">Select MC Company to create contact for</option>
              <option *ngFor="let mc of availableMCCompanies" [value]="mc.id">
                {{ mc.name }}
              </option>
            </select>
            <small style="font-size: 12px; color: var(--ap-text-muted, #6c757d); margin-top: 5px; display: block;">
              This contact will be created for the selected MC company
            </small>
          </div>
          
          <div class="form-group" *ngIf="isGodUser()">
            <label for="rc_company_id">RC Company *</label>
            <select id="rc_company_id" formControlName="rc_company_id" (change)="onRCCompanyChange($event)">
              <option value="">Select RC Company</option>
              <option *ngFor="let rc of availableRCCompanies" [value]="rc.id">
                {{ rc.name }}
              </option>
            </select>
          </div>
          
          <div class="form-group" *ngIf="isGodUser() && selectedRCCompany">
            <label for="mc_company_id">MC Company *</label>
            <select id="mc_company_id" formControlName="mc_company_id">
              <option value="">Select MC Company</option>
              <option *ngFor="let mc of availableMCCompanies" [value]="mc.id">
                {{ mc.name }}
              </option>
            </select>
            <small style="font-size: 12px; color: var(--ap-text-muted, #6c757d); margin-top: 5px; display: block;">
              Creating for: {{ getSelectedRCName() }} → {{ getSelectedMCName() }}
            </small>
          </div>

          <div class="form-group">
            <label for="stage_id">Contact Stage <span class="optional">(Optional)</span></label>
            <select id="stage_id" formControlName="stage_id">
              <option value="">Select a Stage</option>
              <option *ngFor="let stage of availableStages; trackBy: trackStageById" [value]="stage._id">
                {{ stage.name }}
              </option>
            </select>
          </div>
          
          <div class="form-group">
            <label for="notes">Notes <span class="optional">(Optional)</span></label>
            <textarea id="notes" formControlName="notes" placeholder="Enter notes" rows="3"></textarea>
          </div>
          
        
          
          <div class="form-actions">
            <button type="submit" [disabled]="contactForm.invalid">{{ editingContact ? 'Update' : 'Create' }}</button>
            <button type="button" class="cancel-button" (click)="cancelForm()">Cancel</button>
          </div>
        </form>
      </div>
      
      
      <div class="contacts-grid" *ngIf="!loading && !error && contacts.length > 0">
        <div class="contact-card" *ngFor="let contact of contacts">
          <div class="contact-header">
            <div class="contact-main-info">
              <div class="contact-name-row">
                <h3 class="contact-name">{{ contact.first_name }} {{ contact.last_name || '' }}</h3>
                <div class="stage-info">
                  <div class="stage-badge" *ngIf="contact.stage && isStageActive(contact.stage._id)">
                    <span class="badge">{{ contact.stage.name }}</span>
                  </div>
                  <div class="stage-update-status" *ngIf="getStageUpdateText(contact)">
                    <small>{{ getStageUpdateText(contact) }}</small>
                  </div>
                  <!-- Empty placeholder to maintain alignment when no stage info -->
                  <div class="stage-placeholder" *ngIf="!contact.stage || !isStageActive(contact.stage._id)"></div>
                </div>
              </div>
              <div class="contact-meta-info">
                <p class="contact-job-title" *ngIf="contact.job_title">{{ contact.job_title }}</p>
                <div class="company-badge" *ngIf="isRCUser() && contact.company?.name">
                  <span class="company-name">{{ contact.company?.name }}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div class="contact-details">
            <div class="contact-detail" *ngIf="contact.email">
              <span class="detail-label">Email:</span>
              <span class="detail-value">{{ contact.email }}</span>
            </div>
            <div class="contact-detail" *ngIf="contact.mobile">
              <span class="detail-label">Mobile:</span>
              <span class="detail-value">{{ contact.mobile }}</span>
            </div>
            <div class="contact-detail" *ngIf="contact.cv_file_name">
              <span class="detail-label">CV:</span>
              <div class="cv-info">
                <div class="cv-dropdown" [class.open]="selectedDropdownContact === contact.id">
                  <div class="cv-filename-row" (click)="toggleCVDropdown(contact)">
                    <div class="cv-file-details">
                      <span class="cv-filename">{{ contact.cv_file_name }}</span>
                      <small class="cv-upload-date" *ngIf="contact.cv_uploaded_at">
                        Uploaded {{ getRelativeTime(contact.cv_uploaded_at) }}
                      </small>
                    </div>
                    <div class="cv-dropdown-icon">⋮</div>
                  </div>
                  <div class="cv-dropdown-menu" *ngIf="selectedDropdownContact === contact.id">
                    <div class="cv-menu-item" (click)="viewCV(contact)">
                      <span>View</span>
                    </div>
                    <div class="cv-menu-item" (click)="downloadCV(contact)">
                      <span>Download</span>
                    </div>
                    <div class="cv-menu-item" (click)="replaceCV(contact)">
                      <span>Replace</span>
                    </div>
                    <div class="cv-menu-item delete-item" (click)="deleteCV(contact)">
                      <span>Delete</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="contact-actions">
            <button 
              class="conversation-button" 
              [class.disabled]="!canCreateConversationWithContact(contact)"
              [disabled]="!canCreateConversationWithContact(contact)"
              [title]="isContactFromMC(contact) ? 'Cannot create conversations with supervised MC contacts' : ''"
              (click)="handleConversationAction(contact)">
              {{ getConversationButtonText(contact) }}
              <span *ngIf="isContactFromMC(contact)" class="mc-indicator">(MC Contact - Read Only)</span>
            </button>
            <button class="edit-button" (click)="editContact(contact)">Edit</button>
            <button class="delete-button" (click)="confirmDelete(contact)">Delete</button>
          </div>
        </div>
      </div>
      
      <div *ngIf="!loading && !error && contacts.length === 0" class="no-results">
        No contacts found for your company.
      </div>
      
      <div *ngIf="confirmingDelete" class="confirm-dialog">
        <div class="confirm-dialog-content">
          <h3>Confirm Delete</h3>
          <p>Are you sure you want to delete this contact: <strong>{{ contactToDelete?.first_name }} {{ contactToDelete?.last_name || '' }}</strong>?</p>
          <div class="confirm-actions">
            <button class="confirm-button" (click)="deleteContact()">Yes, Delete</button>
            <button class="cancel-button" (click)="cancelDelete()">Cancel</button>
          </div>
        </div>
      </div>
      
      <!-- Conversation Creation Modal -->
      <div *ngIf="showConversationModal" class="conversation-modal" (click)="cancelConversationModal()">
        <div class="conversation-modal-content" (click)="$event.stopPropagation()">
          <!-- Header -->
          <div class="nc-header">
            <span class="nc-title">New conversation</span>
            <button class="nc-close-btn" (click)="cancelConversationModal()" aria-label="Close">&#x2715;</button>
          </div>

          <!-- To field -->
          <div class="nc-to-field">
            <span class="nc-to-label">To:</span>
            <span class="nc-contact-name">{{ getContactDisplayName(selectedContact) }}</span>
            <span class="nc-contact-divider" *ngIf="selectedChannelCategory === 'Email' ? selectedContact?.email : (selectedContact?.mobile || selectedContact?.email)">&middot;</span>
            <span class="nc-contact-info">{{ selectedChannelCategory === 'Email' ? (selectedContact?.email || '') : (selectedContact?.mobile || selectedContact?.email || '') }}</span>
          </div>

          <!-- Step 1: Channel category chips -->
          <div class="nc-step">
            <div class="nc-step-header">
              <span class="nc-step-label">via</span>
            </div>
            <div class="nc-channel-chips">
              <button
                *ngFor="let cat of getAllChannelCategories()"
                class="nc-chip"
                [class.nc-chip-selected]="selectedChannelCategory === cat"
                [class.nc-chip-disabled]="selectedContact && hasConversationForCategory(selectedContact, cat)"
                [disabled]="selectedContact && hasConversationForCategory(selectedContact, cat)"
                (click)="selectChannelCategory(cat)">
                <span class="nc-chip-label">{{ cat }}</span>
              </button>
            </div>
          </div>

          <!-- Step 2: Pick a specific channel (shown after category selected) -->
          <div class="nc-step nc-step-channel" *ngIf="selectedChannelCategory">
            <div class="nc-step-header">
              <span class="nc-step-label">Select {{ selectedChannelCategory }} channel</span>
            </div>

            <!-- Channel cards -->
            <div class="nc-channel-cards" *ngIf="filteredChannels.length > 0">
              <button
                *ngFor="let ch of filteredChannels"
                class="nc-channel-card"
                [class.nc-channel-card-selected]="selectedChannel?.id === ch.id"
                (click)="selectChannel(ch.id)">
                <span class="nc-channel-card-name">{{ ch.name }}</span>
                <span class="nc-channel-card-id">{{ ch.mobile || ch.email || '' }}</span>
              </button>
            </div>

            <div class="nc-no-channels" *ngIf="filteredChannels.length === 0">
              No {{ selectedChannelCategory }} channels configured for your account.
            </div>
          </div>

          <!-- First message section (shown after "Start First Conversation" clicked) -->
          <ng-container *ngIf="showFirstMessageInput">
            <input
              *ngIf="selectedChannelCategory === 'Email'"
              class="nc-subject-input"
              type="text"
              [(ngModel)]="emailSubject"
              placeholder="Subject (Email)...">
            <textarea
              class="nc-message-textarea"
              [(ngModel)]="firstMessage"
              placeholder="Type your first message...">
            </textarea>
          </ng-container>

          <!-- Footer actions -->
          <div class="nc-footer">
            <div class="nc-footer-left">
              <button
                *ngIf="selectedChannelCategory"
                class="nc-back-btn"
                (click)="goBack()"
                type="button"
                aria-label="Back">
                &#8592; Back
              </button>
              <button
                *ngIf="showFirstMessageInput"
                class="nc-insert-template-btn"
                (click)="openNewConvoTemplateModal()"
                type="button">
                Insert template
              </button>
            </div>
            <div class="nc-footer-right">
              <!-- Start First Conversation (shown when channel selected, no message input yet) -->
              <button
                *ngIf="selectedChannel && !showFirstMessageInput"
                class="nc-start-btn"
                (click)="startFirstConversation()"
                type="button">
                Start First Conversation
              </button>

              <!-- Send button (shown when message input is visible) -->
              <button
                *ngIf="showFirstMessageInput"
                class="nc-send-btn"
                [disabled]="!firstMessage.trim() || creatingConversation"
                (click)="createConversation()"
                type="button">
                <span *ngIf="creatingConversation" class="ap-spinner"></span>
                {{ creatingConversation ? 'Sending...' : 'Send' }}
              </button>

              <!-- Create Conversation (shown when channel selected but NOT in message mode) -->
              <button
                *ngIf="selectedChannel && !showFirstMessageInput"
                class="nc-create-btn"
                [disabled]="creatingConversation"
                (click)="createConversation()"
                type="button">
                <span *ngIf="creatingConversation" class="ap-spinner"></span>
                {{ creatingConversation ? 'Creating...' : 'Create Conversation' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Template modal for new conversation -->
      <div class="conversation-modal" *ngIf="showNewConvoTemplateModal" (click)="showNewConvoTemplateModal = false">
        <div class="nc-template-modal-wrapper" (click)="$event.stopPropagation()">
          <app-template-quick-access
            [channelType]="getTemplateChannelType()"
            (templateSelected)="onNewConvoTemplateSelected($event)"
            (cancel)="showNewConvoTemplateModal = false">
          </app-template-quick-access>
        </div>
      </div>
      

      <!-- Hidden file input for CV replacement -->
      <input #cvFileInput 
             type="file" 
             accept=".pdf,.doc,.docx,.txt" 
             (change)="onReplacementCVFileSelected($event)" 
             style="display: none;">

      <!-- CV Delete Confirmation Modal -->
      <div *ngIf="showDeleteCVModal" class="modal-overlay">
        <div class="modal-content delete-modal">
          <div class="modal-header">
            <h3>Delete CV</h3>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete the CV for <strong>{{ contactForCVDeletion?.first_name }} {{ contactForCVDeletion?.last_name }}</strong>?</p>
            <p class="warning-text">This action cannot be undone.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-cancel" (click)="cancelDeleteCV()">Cancel</button>
            <button type="button" class="btn-delete" (click)="confirmDeleteCV()">Delete</button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- CV Viewer Component -->
    <app-cv-viewer 
      [isVisible]="showCVViewer"
      [contactId]="selectedContactForCV?.id || null"
      [fileName]="selectedContactForCV?.cv_file_name || null"
      (closeViewer)="closeCVViewer()">
    </app-cv-viewer>
  `,
  styleUrls: ['./contacts.component.scss']
})
export class ContactsComponent implements OnInit, OnDestroy {
  contacts: Contact[] = [];
  currentUser: User | null = null;
  loading: boolean = false;
  error: string | null = null;
  showForm: boolean = false;
  editingContact: Contact | null = null;
  contactForm: FormGroup;
  confirmingDelete: boolean = false;
  contactToDelete: Contact | null = null;
  
  // Conversation-related properties
  conversations: Conversation[] = [];
  availableChannels: Channel[] = [];
  availableStages: ContactStage[] = [];
  showConversationModal: boolean = false;
  selectedContact: Contact | null = null;
  conversationForm: FormGroup;
  creatingConversation: boolean = false;

  // New conversation modal state
  selectedChannelCategory: string | null = null;
  selectedChannel: Channel | null = null;
  showFirstMessageInput: boolean = false;
  firstMessage: string = '';
  emailSubject: string = '';
  showNewConvoTemplateModal: boolean = false;
  filteredChannels: Channel[] = [];
  
  // CV Upload properties
  @ViewChild('cvUpload') cvUploadComponent!: CVUploadComponent;
  @ViewChild('cvFileInput') cvFileInput!: any;
  selectedCVFile: File | null = null;
  existingCvFile: CVFile | null = null;
  cvValidationError: string | null = null;
  
  // Search functionality
  searchQuery: string = '';
  allContacts: Contact[] = []; // Store all contacts for counting and searching
  searchTimeout: any;
  selectedStage: string = 'all'; // Track selected stage for overview highlighting
  
  // Company dropdown functionality
  availableRCCompanies: CompanyDropdownOption[] = [];
  availableMCCompanies: CompanyDropdownOption[] = [];
  selectedRCCompany: string = '';
  
  // CV properties
  selectedDropdownContact: string | null = null;
  contactForCVReplacement: Contact | null = null;
  showDeleteCVModal: boolean = false;
  contactForCVDeletion: Contact | null = null;
  
  // CV Viewer properties
  showCVViewer: boolean = false;
  selectedContactForCV: Contact | null = null;
  
  // Subscription management
  private subscriptions = new Subscription();
  
  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private authService: AuthService,
    private errorService: ErrorService,
    private conversationService: ConversationService,
    private wsService: WebSocketService,
    private stageService: ContactStageService,
    private rcOversightService: RCOversightService,
    private godService: GodService,
    private permissionService: PermissionService,
    private rcDataSyncService: RCDataSyncService,
    private mcDataSyncService: MCDataSyncService
  ) {
    this.contactForm = this.fb.group({
      id: [''],
      first_name: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
      last_name: ['', [Validators.required, Validators.maxLength(50)]],
      email: ['', [Validators.required, Validators.email]],
      mobile: ['', [Validators.required, Validators.pattern(/^\+[0-9]{11,14}$/)]],
      job_title: ['', Validators.maxLength(100)],
      stage_id: [''],
      notes: ['', Validators.maxLength(500)],
      rc_company_id: [''],
      mc_company_id: ['']
    });
    
    this.conversationForm = this.fb.group({
      channel_id: ['', Validators.required]
    });
  }
  
  ngOnInit(): void {
    // Subscribe to current user to get company ID
    this.subscriptions.add(
      this.authService.currentUser$.subscribe(user => {
        this.currentUser = user;
        if (user && user.company_id) {
          this.loadContacts();
          this.loadConversations();
          this.loadChannels();
          this.loadStages();
          this.loadCompanyDropdowns(); // Load company dropdowns for RC/God users
        }
      })
    );
    
    // Subscribe to conversation updates to refresh button text
    this.subscriptions.add(
      this.conversationService.conversationUpdated$.subscribe(updatedConversation => {
        console.log('[ContactsComponent] Received conversation update:', updatedConversation);
        
        // Check if this is a new or updated conversation that affects our contact list
        if (updatedConversation.id && updatedConversation.initiator_id) {
          const existingIndex = this.conversations.findIndex(conv => conv.id === updatedConversation.id);
          
          if (existingIndex !== -1) {
            // Update existing conversation
            this.conversations[existingIndex] = { ...this.conversations[existingIndex], ...updatedConversation };
          } else {
            // Add new conversation to the list
            this.conversations.push(updatedConversation);
          }
          
          console.log('[ContactsComponent] Updated conversations list, total conversations:', this.conversations.length);
        }
      })
    );

    // RC Data Synchronization: Subscribe to real-time contact updates
    this.subscriptions.add(
      this.rcDataSyncService.contactUpdates$.subscribe(syncEvent => {
        if (this.isRCSuperAdmin()) {
          console.log('[ContactsComponent] Received RC contact sync event:', syncEvent);
          
          // Refresh contact list when other RC Super Admins make changes
          if (syncEvent.event === 'created' || syncEvent.event === 'updated' || syncEvent.event === 'deleted') {
            console.log('[ContactsComponent] Refreshing contacts due to RC sync event');
            this.loadContacts();
          }
        }
      })
    );

    // MC Admin Data Synchronization: Subscribe to real-time contact updates
    this.subscriptions.add(
      this.mcDataSyncService.contactUpdates$.subscribe(syncEvent => {
        if (this.isMCAdmin()) {
          console.log('[ContactsComponent] Received MC Admin contact sync event:', syncEvent);
          
          // Refresh contact list when other MC Admins in same company make changes
          if (syncEvent.event === 'created' || syncEvent.event === 'updated' || syncEvent.event === 'deleted') {
            console.log('[ContactsComponent] Refreshing contacts due to MC Admin sync event');
            this.loadContacts();
          }
        }
      })
    );

    // Set RC Super Admin and MC Admin status for data sync services
    this.subscriptions.add(
      this.authService.currentUser$.subscribe(user => {
        if (user) {
          const isRCSuperAdmin = user.role === UserRole.OWNER && user.company_type === 'RC';
          this.rcDataSyncService.setRCSuperAdminStatus(isRCSuperAdmin);
          
          const isMCAdmin = user.role === UserRole.ADMIN && user.company_type === 'MC';
          this.mcDataSyncService.setMCAdminStatus(isMCAdmin, user.company_id);
        }
      })
    );
    
    // Subscribe to new message events as they often indicate new conversations
    this.subscriptions.add(
      this.conversationService.newMessage$.subscribe(event => {
        console.log('[ContactsComponent] Received new message event:', event);
        
        if (event.conversationId && event.action === 'created') {
          // Check if we already have this conversation in our list
          const existingConversation = this.conversations.find(conv => conv.id === event.conversationId);
          
          if (!existingConversation) {
            // This might be a new conversation, refresh our conversation list
            console.log('[ContactsComponent] New message in unknown conversation, refreshing conversation list');
            this.loadConversations();
          }
        }
      })
    );
    
    // Subscribe to WebSocket messages for contact updates
    this.subscriptions.add(
      this.wsService.messages$.subscribe(message => {
        console.log('[ContactsComponent] Received WebSocket message:', message);
        
        // Handle contact creation events
        if (message.type === 'contact' && message['event'] === 'created') {
          console.log('[ContactsComponent] New contact created, refreshing contact list');
          // Only refresh if it's for our company
          if (this.currentUser && message['company_id'] === this.currentUser.company_id) {
            this.loadContacts();
            
            // Show appropriate message based on source
            if (message['source'] === 'candidate_application') {
              this.errorService.showInfo('New candidate contact added successfully', true, 5000);
            } else if (message['source'] !== 'manual') {
              this.errorService.showInfo('New contact added via message', true, 3000);
            }
          }
        }
        
        // Handle contact deletion events — refresh conversations since they are cascade-deleted
        if (message.type === 'contact' && message['event'] === 'deleted') {
          if (this.currentUser && message['company_id'] === this.currentUser.company_id) {
            const deletedId = message['contact_id'];
            this.contacts = this.contacts.filter(c => c.id !== deletedId);
            this.conversations = this.conversations.filter(conv => conv.initiator_id !== deletedId);
          }
        }

        // Handle conversation creation events
        if (message.type === 'conversation' && message['event'] === 'created') {
          console.log('[ContactsComponent] New conversation created, refreshing conversation list');
          // Only refresh if it's for our company
          if (this.currentUser && message['company_id'] === this.currentUser.company_id) {
            this.loadConversations();
          }
        }
      })
    );
  }
  
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
  
  refreshData(): void {
    this.loadContacts();
    this.loadConversations();
    this.loadChannels();
    this.loadStages();
  }
  
  loadContacts(): void {
    if (!this.currentUser?.company_id) {
      this.error = 'No company ID found for current user';
      return;
    }
    
    this.loading = true;
    this.error = null;
    
    // Check if user is RC (Super Admin) and should load MC contacts
    if (this.isRCUser()) {
      console.log('[ContactsComponent] Loading contacts for RC user - calling loadRCContacts()');
      this.loadRCContacts();
    } else {
      console.log('[ContactsComponent] Loading contacts for regular user - calling loadRegularContacts()');
      this.loadRegularContacts();
    }
  }

  private loadRegularContacts(): void {
    // Load contacts from user's own company (existing logic)
    const allContactsUrl = `${config.apiUrl}/api/contacts?company_id=${this.currentUser?.company_id}`;
    
    this.http.get<any>(allContactsUrl)
      .subscribe({
        next: (response) => {
          if (response.success) {
            // Sort contacts by created_at in descending order (latest first)
            this.allContacts = (response.data || []).sort((a: any, b: any) => {
              const dateA = new Date(a.created_at || a.createdAt || 0).getTime();
              const dateB = new Date(b.created_at || b.createdAt || 0).getTime();
              return dateB - dateA; // Descending order (latest first)
            });
            this.applySearch(); // Apply search after loading
            console.log('[ContactsComponent] Loaded regular contacts:', this.allContacts.length);
          } else {
            this.error = response.message || 'Failed to load contacts';
            this.errorService.showError(this.error);
          }
          this.loading = false;
        },
        error: (err) => {
          this.error = err.error?.message || 'An error occurred while loading contacts';
          this.errorService.handleHttpError(err, 'Failed to load contacts');
          this.loading = false;
        }
      });
  }

  private loadRCContacts(): void {
    // Load contacts from all supervised MC companies (RC oversight)
    console.log('[ContactsComponent] loadRCContacts() called - making API call to getMCContacts()');
    this.subscriptions.add(
      this.rcOversightService.getMCContacts().subscribe({
        next: (response) => {
          console.log('[ContactsComponent] RC oversight API response:', response);
          if (response.success) {
            // Sort contacts by created_at in descending order (latest first)
            this.allContacts = (response.data || []).sort((a: any, b: any) => {
              const dateA = new Date(a.created_at || a.createdAt || 0).getTime();
              const dateB = new Date(b.created_at || b.createdAt || 0).getTime();
              return dateB - dateA; // Descending order (latest first)
            });
            this.applySearch(); // Apply search after loading
            console.log('[ContactsComponent] Loaded RC oversight contacts:', this.allContacts.length, this.allContacts);
          } else {
            console.error('[ContactsComponent] RC oversight API returned error:', response.message);
            this.error = response.message || 'Failed to load RC contacts';
            this.errorService.showError(this.error);
          }
          this.loading = false;
        },
        error: (err) => {
          console.error('[ContactsComponent] RC oversight API call failed:', err);
          this.error = err.error?.message || 'An error occurred while loading RC contacts';
          this.errorService.handleHttpError(err, 'Failed to load RC contacts');
          this.loading = false;
        }
      })
    );
  }

  loadStages(): void {
    this.subscriptions.add(
      this.stageService.getStages().subscribe({
        next: (response) => {
          if (response.success && Array.isArray(response.data)) {
            this.availableStages = response.data;
          }
        },
        error: (error) => {
          console.error('Error loading stages:', error);
        }
      })
    );
  }
  
  showAddContactForm(): void {
    this.editingContact = null;
    this.contactForm.reset();
    this.loadStages(); // Refresh stages when opening form
    this.showForm = true;
  }
  
  editContact(contact: Contact): void {
    this.editingContact = contact;
    this.contactForm.patchValue({
      id: contact.id || '',
      first_name: contact.first_name,
      last_name: contact.last_name || '',
      email: contact.email || '',
      mobile: contact.mobile || '',
      job_title: contact.job_title || '',
      stage_id: contact.stage?._id || '',
      notes: contact.notes || ''
    });

    // Setup existing CV data
    this.setupExistingCVData(contact);

    // Refresh stages to ensure dropdown is up-to-date, and re-patch stage_id after options render
    this.subscriptions.add(
      this.stageService.getStages().subscribe({
        next: (response) => {
          if (response.success && Array.isArray(response.data)) {
            this.availableStages = response.data;
          }
          // Re-patch stage_id after stages are refreshed to ensure it matches an option
          setTimeout(() => {
            this.contactForm.patchValue({
              stage_id: contact.stage?._id || ''
            });
          });
        },
        error: (error) => {
          console.error('Error loading stages:', error);
        }
      })
    );

    this.showForm = true;
  }
  
  private setupExistingCVData(contact: Contact): void {
    this.selectedCVFile = null;
    this.cvValidationError = null;
    
    if (contact.cv_file_name && contact.cv_file_size && contact.cv_uploaded_at) {
      this.existingCvFile = {
        name: contact.cv_file_name,
        size: contact.cv_file_size,
        uploadedAt: new Date(contact.cv_uploaded_at)
      };
    } else {
      this.existingCvFile = null;
    }
  }
  
  saveContact(): void {
    if (this.contactForm.invalid || !this.currentUser?.company_id) return;
    
    // Prepare data for API (automatically set company_id from current user)
    const contactData = {
      first_name: this.contactForm.get('first_name')?.value,
      last_name: this.contactForm.get('last_name')?.value,
      email: this.contactForm.get('email')?.value,
      mobile: this.contactForm.get('mobile')?.value,
      job_title: this.contactForm.get('job_title')?.value || null,
      stage: this.contactForm.get('stage_id')?.value || null,
      company_id: this.currentUser.company_id,
      notes: this.contactForm.get('notes')?.value || null
    };
    
    let request;
    
    if (this.editingContact && this.editingContact.id) {
      // Update existing contact
      request = this.http.put<any>(`${config.apiUrl}/api/contacts/${this.editingContact.id}`, contactData);
    } else {
      // Create new contact
      request = this.http.post<any>(`${config.apiUrl}/api/contacts`, contactData);
    }
    
    request.subscribe({
      next: async (response) => {
        if (response.success) {
          // Handle CV upload for new contacts
          if (!this.editingContact && this.selectedCVFile && response.data) {
            try {
              await this.uploadCVForNewContact(response.data);
            } catch (cvError) {
              console.error('CV upload failed for new contact:', cvError);
              this.errorService.showError('Contact created but CV upload failed. You can upload it later.');
            }
          }
          
          this.contactForm.reset();
          this.showForm = false;
          this.loadContacts(); // Reload contacts
          this.errorService.showInfo('Contact saved successfully', true, 3000);
        } else {
          this.error = response.message || 'Failed to save contact';
          this.errorService.showError(this.error);
        }
      },
      error: (err) => {
        this.error = err.error?.message || 'An error occurred while saving contact';
        this.errorService.handleHttpError(err, 'Failed to save contact');
      }
    });
  }
  
  confirmDelete(contact: Contact): void {
    this.contactToDelete = contact;
    this.confirmingDelete = true;
  }
  
  deleteContact(): void {
    if (!this.contactToDelete || !this.contactToDelete.id) return;
    
    this.http.delete<any>(`${config.apiUrl}/api/contacts/${this.contactToDelete.id}`)
      .subscribe({
        next: (response) => {
          if (response.success) {
            // Remove contact from array and refresh conversations (which were cascade-deleted server-side)
            this.contacts = this.contacts.filter(c => c.id !== this.contactToDelete?.id);
            this.loadConversations();
            this.errorService.showInfo('Contact deleted successfully', true, 3000);
          } else {
            this.error = response.message || 'Failed to delete contact';
            this.errorService.showError(this.error);
          }
          this.cancelDelete();
        },
        error: (err) => {
          this.error = err.error?.message || 'An error occurred while deleting contact';
          this.errorService.handleHttpError(err, 'Failed to delete contact');
          this.cancelDelete();
        }
      });
  }
  
  cancelDelete(): void {
    this.contactToDelete = null;
    this.confirmingDelete = false;
  }
  
  cancelForm(): void {
    this.showForm = false;
    this.editingContact = null;
    // Reset CV form data
    this.selectedCVFile = null;
    this.existingCvFile = null;
    this.cvValidationError = null;
  }
  
  // CV Upload event handlers
  onCVFileSelected(file: File): void {
    this.selectedCVFile = file;
  }
  
  onCVUploaded(cvFile: CVFile): void {
    this.existingCvFile = cvFile;
    this.selectedCVFile = null;
    this.cvValidationError = null;
  }
  
  onCVDeleted(): void {
    this.existingCvFile = null;
    this.selectedCVFile = null;
    this.cvValidationError = null;
  }
  
  onCVValidationChange(error: string | null): void {
    this.cvValidationError = error;
  }
  
  private async uploadCVForNewContact(contactId: string): Promise<void> {
    if (!this.selectedCVFile) return;
    
    const formData = new FormData();
    formData.append('cv', this.selectedCVFile);
    
    const response = await this.http.post<any>(
      `${config.apiUrl}/api/contacts/${contactId}/cv`,
      formData
    ).toPromise();
    
    if (response.success) {
      console.log('CV uploaded successfully for new contact');
    }
  }

  // Conversation-related methods
  loadConversations(): void {
    if (!this.currentUser?.company_id) return;
    
    this.subscriptions.add(
      this.conversationService.getConversations().subscribe({
        next: (conversations) => {
          this.conversations = conversations;
          console.log('[ContactsComponent] Loaded conversations using ConversationService, total:', this.conversations.length);
        },
        error: (error) => {
          console.error('Error loading conversations:', error);
        }
      })
    );
  }

  private loadConversationsAndAutoOpen(contactId: string): void {
    if (!this.currentUser?.company_id || !contactId) return;
    
    this.subscriptions.add(
      this.conversationService.getConversations().subscribe({
        next: (conversations) => {
          this.conversations = conversations;
          console.log('[ContactsComponent] Conversations refreshed using ConversationService, total:', this.conversations.length);
          
          // Find the most recent conversation for this contact and auto-open it
          const contactConversation = this.conversations.find(conv => 
            conv.initiator_id === contactId && conv.status !== 'Deleted'
          );
          
          if (contactConversation) {
            console.log('[ContactsComponent] Auto-opening conversation for contact:', contactId, 'conversation:', contactConversation.id);
            setTimeout(() => {
              this.conversationService.setActiveConversation(contactConversation);
            }, 100);
          } else {
            console.warn('[ContactsComponent] No conversation found for contact:', contactId);
          }
        },
        error: (error) => {
          console.error('Error loading conversations:', error);
        }
      })
    );
  }

  loadChannels(): void {
    // Load channels based on user permissions (no company_id filter needed)
    console.log('🔍 [ContactsComponent] ===== LOADING CHANNELS START =====');
    console.log(`[ContactsComponent] Loading channels for user: ${this.currentUser?.name} (${this.currentUser?.email})`);
    console.log(`[ContactsComponent] User role: ${this.currentUser?.role}, Company: ${this.currentUser?.company}, Company ID: ${this.currentUser?.company_id}`);
    
    this.http.get<any>(`${config.apiUrl}/api/channels`)
      .subscribe({
        next: (response) => {
          console.log(`[ContactsComponent] API Response:`, response);
          if (response.success) {
            // Filter channels based on user role and channel status
            let channels = response.data;
            console.log(`[ContactsComponent] Raw channels from API:`, channels.map((c: any) => ({ id: c.id, name: c.name, status: c.status })));
            
            // For MC users, only show Active channels (exclude Inactive/Suspended channels)
            if (this.isMCUser()) {
              const beforeFilter = channels.length;
              channels = channels.filter((channel: Channel) => 
                channel.status === 'Active'
              );
              console.log(`[ContactsComponent] MC user - filtered from ${beforeFilter} to ${channels.length} active channels`);
              console.log(`[ContactsComponent] Final channels for MC user:`, channels.map((c: any) => ({ id: c.id, name: c.name, status: c.status })));
            } else {
              console.log(`[ContactsComponent] Non-MC user - keeping all ${channels.length} channels`);
            }
            
            this.availableChannels = channels;
            console.log(`[ContactsComponent] Final availableChannels set:`, this.availableChannels.length, this.availableChannels);
          } else {
            console.error('Failed to load channels:', response.message);
          }
        },
        error: (err) => {
          console.error('[ContactsComponent] Error loading channels:', err);
          console.error('[ContactsComponent] HTTP Status:', err.status);
          console.error('[ContactsComponent] Error details:', err.error);
        }
      });
  }

  hasConversationForAllCategories(contact: Contact): boolean {
    const allCategories = ['SMS', 'Email', 'WhatsApp'];
    return allCategories.every(cat => {
      const channelIdsOfType = this.availableChannels
        .filter(c => c.channel_type === cat)
        .map(c => c.id);
      return channelIdsOfType.some(chId =>
        this.conversations.some(conv =>
          conv.initiator_id === contact.id &&
          conv.channel_id === chId &&
          conv.status !== 'Deleted'
        )
      );
    });
  }

  hasConversationForCategory(contact: Contact, category: string): boolean {
    const channelIdsOfType = this.availableChannels
      .filter(c => c.channel_type === category)
      .map(c => c.id);
    return channelIdsOfType.some(chId =>
      this.conversations.some(conv =>
        conv.initiator_id === contact.id &&
        conv.channel_id === chId &&
        conv.status !== 'Deleted'
      )
    );
  }

  getConversationButtonText(contact: Contact): string {
    return this.hasConversationForAllCategories(contact) ? 'Open Conversation' : 'New Conversation';
  }

  // Check if contact belongs to an MC company (for RC users)
  isContactFromMC(contact: Contact): boolean {
    if (!this.isRCUser()) return false;
    
    // If contact has company_id different from current user's company_id, it's from MC
    return contact.company_id !== this.currentUser?.company_id;
  }

  // Check if RC user can create conversation with this contact
  canCreateConversationWithContact(contact: Contact): boolean {
    if (!this.isRCUser()) return true; // Non-RC users can create conversations with any contact
    
    // RC users can only create conversations with their own contacts (not MC contacts)
    return !this.isContactFromMC(contact);
  }

  handleConversationAction(contact: Contact): void {
    // Check if RC user can create conversation with this contact
    if (!this.canCreateConversationWithContact(contact)) {
      console.log('[ContactsComponent] Cannot create conversation with MC contact:', contact);
      return;
    }

    if (this.hasConversationForAllCategories(contact) && contact.id) {
      // All 3 categories covered — open the most recent conversation
      const existingConversation = this.conversations.find(conv =>
        conv.initiator_id === contact.id && conv.status !== 'Deleted'
      );
      if (existingConversation) {
        console.log('Opening conversation for contact:', contact.id, 'conversation:', existingConversation.id);
        this.conversationService.setActiveConversation(existingConversation);
      }
    } else {
      // Show modal to create new conversation — refresh conversations first so chip state is current
      this.selectedContact = contact;
      this.conversationForm.reset();
      this.conversationForm.patchValue({ channel_id: '' });
      this.loadConversations();
      this.showConversationModal = true;
    }
  }

  getContactDisplayName(contact: Contact | null): string {
    if (!contact) return '';
    return `${contact.first_name} ${contact.last_name || ''}`.trim();
  }

  getAllChannelCategories(): string[] {
    return ['SMS', 'Email', 'WhatsApp'];
  }

  getTemplateChannelType(): string {
    if (!this.selectedChannelCategory) return '';
    return this.selectedChannelCategory.toLowerCase();
  }

  selectChannelCategory(category: string): void {
    this.selectedChannelCategory = category;
    this.filteredChannels = this.availableChannels.filter(c => c.channel_type === category);
    this.selectedChannel = null;
    this.showFirstMessageInput = false;
    this.firstMessage = '';
    this.emailSubject = '';
  }

  selectChannel(channelId: string): void {
    this.selectedChannel = this.filteredChannels.find(c => c.id === channelId) || null;
    this.showFirstMessageInput = false;
    this.firstMessage = '';
    this.emailSubject = '';
  }

  startFirstConversation(): void {
    this.showFirstMessageInput = true;
  }

  goBack(): void {
    if (this.showFirstMessageInput) {
      // Step back: hide message input, keep channel selected
      this.showFirstMessageInput = false;
      this.firstMessage = '';
      this.emailSubject = '';
    } else if (this.selectedChannel) {
      // Step back: deselect channel, keep category selected
      this.selectedChannel = null;
    } else if (this.selectedChannelCategory) {
      // Step back: deselect category
      this.selectedChannelCategory = null;
      this.filteredChannels = [];
    }
  }

  openNewConvoTemplateModal(): void {
    this.showNewConvoTemplateModal = true;
  }

  onNewConvoTemplateSelected(template: CommunicationTemplate): void {
    this.firstMessage = template.body;
    if (this.selectedChannelCategory === 'Email' && template.subject) {
      this.emailSubject = template.subject;
    }
    this.showNewConvoTemplateModal = false;
  }

  createConversation(): void {
    if (!this.selectedContact || !this.selectedChannel) return;

    this.creatingConversation = true;

    const normalizedMobile = this.selectedContact.mobile?.trim() || '';
    const contactName = this.getContactDisplayName(this.selectedContact);
    const isEmailChannel = this.selectedChannel.channel_type === 'Email';
    const external_ref = isEmailChannel
      ? (this.selectedContact.email?.trim() || '')
      : normalizedMobile;

    const conversationData = {
      title: `Conversation with ${contactName}`,
      initiator_id: this.selectedContact.id,
      initiator_type: 'Contact',
      channel_id: this.selectedChannel.id,
      company_id: this.currentUser?.company_id,
      external_ref,
      status: 'Active',
      priority: 'Normal',
      tags: [],
      metadata: JSON.stringify({
        contact: {
          id: this.selectedContact.id,
          name: contactName,
          mobile: normalizedMobile
        }
      })
    };

    this.http.post<any>(`${config.apiUrl}/api/conversations`, conversationData)
      .subscribe({
        next: (response) => {
          if (response.success) {
            const conversationId: string = response.data?.id || response.data?._id;
            const sendMessage = this.showFirstMessageInput && this.firstMessage.trim() && conversationId;

            if (sendMessage) {
              this.conversationService.sendMessage(conversationId, this.firstMessage.trim()).subscribe({
                next: () => {
                  this.errorService.showInfo('Conversation started and message sent', true, 3000);
                  this._afterConversationCreated(response, conversationData, conversationId);
                },
                error: () => {
                  this.errorService.showInfo('Conversation created (message failed to send)', true, 3000);
                  this._afterConversationCreated(response, conversationData, conversationId);
                }
              });
            } else {
              this.errorService.showInfo('Conversation created successfully', true, 3000);
              this._afterConversationCreated(response, conversationData, conversationId);
            }
          } else {
            this.error = response.message || 'Failed to create conversation';
            this.errorService.showError(this.error);
            this.creatingConversation = false;
          }
        },
        error: (err) => {
          this.error = err.error?.message || 'An error occurred while creating conversation';
          this.errorService.handleHttpError(err, 'Failed to create conversation');
          this.creatingConversation = false;
        }
      });
  }

  private _afterConversationCreated(_response: any, conversationData: any, conversationId: string): void {
    const contactIdToOpen = this.selectedContact?.id;
    this.cancelConversationModal();

    if (conversationId) {
      const newConversation: Partial<Conversation> = {
        id: conversationId,
        title: conversationData.title,
        initiator_id: conversationData.initiator_id,
        channel_id: conversationData.channel_id,
        company_id: conversationData.company_id,
        status: 'Active' as const,
        metadata: conversationData.metadata
      };
      this.conversationService.broadcastConversationUpdate(newConversation, 'contacts-create');
      if (contactIdToOpen) {
        this.loadConversationsAndAutoOpen(contactIdToOpen);
      } else {
        this.loadConversations();
      }
    } else {
      this.loadConversations();
    }
    this.creatingConversation = false;
  }

  cancelConversationModal(): void {
    this.showConversationModal = false;
    this.selectedContact = null;
    this.selectedChannelCategory = null;
    this.selectedChannel = null;
    this.filteredChannels = [];
    this.showFirstMessageInput = false;
    this.firstMessage = '';
    this.emailSubject = '';
    this.showNewConvoTemplateModal = false;
    this.conversationForm.reset();
    this.conversationForm.patchValue({ channel_id: '' });
    this.creatingConversation = false;
  }

  trackStageById(_index: number, stage: ContactStage): string {
    return stage._id;
  }

  isStageActive(stageId: string): boolean {
    return this.availableStages.some(stage => stage._id === stageId);
  }

  onSearchChange(): void {
    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // Debounce search by 300ms
    this.searchTimeout = setTimeout(() => {
      this.applySearch();
    }, 300);
  }

  applySearch(): void {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      // Show all contacts if no search query - reset to 'all' selection
      this.selectedStage = 'all';
      this.contacts = [...this.allContacts];
    } else {
      // When searching, reset to 'all' since we're searching across all contacts
      this.selectedStage = 'all';
      const query = this.searchQuery.toLowerCase().trim();
      this.contacts = this.allContacts.filter(contact => {
        // Search in contact name
        const fullName = `${contact.first_name} ${contact.last_name || ''}`.toLowerCase();
        if (fullName.includes(query)) return true;
        
        // Search in email
        if (contact.email && contact.email.toLowerCase().includes(query)) return true;
        
        // Search in stage name
        if (contact.stage && contact.stage.name.toLowerCase().includes(query)) return true;
        
        return false;
      }).sort((a: any, b: any) => {
        // Maintain latest-first sorting even in search results
        const dateA = new Date(a.created_at || a.createdAt || 0).getTime();
        const dateB = new Date(b.created_at || b.createdAt || 0).getTime();
        return dateB - dateA;
      });
    }
    
    console.log('[ContactsComponent] Search applied:', this.contacts.length, 'results for:', this.searchQuery);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.applySearch();
  }

  getStageContactCount(stageId: string): number {
    return this.allContacts.filter(contact => contact.stage?._id === stageId).length;
  }

  getTotalContactCount(): number {
    return this.allContacts.length;
  }

  selectStageFromOverview(stageId: string): void {
    // Update selected stage for highlighting
    this.selectedStage = stageId;
    
    // Clear search and show only contacts from selected stage
    this.searchQuery = '';
    this.contacts = this.allContacts.filter(contact => {
      if (stageId === 'all') return true;
      return contact.stage?._id === stageId;
    });
  }

  getStageUpdateText(contact: Contact): string {
    console.log('[ContactsComponent] Contact stage data:', {
      id: contact.id,
      stage: contact.stage,
      stage_updated_at: contact.stage_updated_at,
      updated_at: contact.updated_at
    });

    const parts: string[] = [];

    if (contact.stage_updated_at && contact.stage) {
      const timeAgo = this.getRelativeTime(contact.stage_updated_at);
      parts.push(`Stage updated: ${timeAgo}`);
    }

    if (contact.updated_at) {
      const timeAgo = this.getRelativeTime(contact.updated_at);
      parts.push(`Last modified: ${timeAgo}`);
    }

    return parts.join(' | ');
  }

  getRelativeTime(dateString: string): string {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    
    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
    if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    const diffYears = Math.floor(diffMonths / 12);
    return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }


  // Company dropdown helper methods
  isRCUser(): boolean {
    const isRC = this.currentUser?.role === UserRole.OWNER && 
                 this.currentUser?.company_type === 'RC';
    console.log('[ContactsComponent] isRCUser check - Role:', this.currentUser?.role, 'CompanyType:', this.currentUser?.company_type, 'Result:', isRC);
    console.log('[ContactsComponent] Full currentUser:', this.currentUser);
    return isRC;
  }

  isGodUser(): boolean {
    return this.currentUser?.role === UserRole.PLATFORM_OWNER;
  }

  isMCUser(): boolean {
    // MC users have ADMIN role and MC company type, or USER role with MC company type
    return (this.currentUser?.role === UserRole.ADMIN && this.currentUser?.company_type === 'MC') ||
           (this.currentUser?.role === UserRole.MEMBER && this.currentUser?.company_type === 'MC');
  }

  isMCAdmin(): boolean {
    // MC Admin users have ADMIN role and MC company type
    return this.currentUser?.role === UserRole.ADMIN && this.currentUser?.company_type === 'MC';
  }

  isRCSuperAdmin(): boolean {
    return this.currentUser?.role === UserRole.OWNER && this.currentUser?.company_type === 'RC';
  }

  onRCCompanyChange(event: any): void {
    const rcId = event.target.value;
    this.selectedRCCompany = rcId;
    
    // Reset MC selection when RC changes
    this.contactForm.get('mc_company_id')?.setValue('');
    this.availableMCCompanies = [];
    
    if (rcId) {
      this.loadMCsForRC(rcId);
    }
  }

  loadMCsForRC(rcId: string): void {
    this.godService.getMCCompaniesForRCDropdown(rcId).subscribe({
      next: (companies) => {
        this.availableMCCompanies = companies;
      },
      error: (err) => {
        console.error('Error loading MC companies for RC:', err);
        this.error = 'Failed to load MC companies';
      }
    });
  }

  getSelectedRCName(): string {
    const rcId = this.contactForm.get('rc_company_id')?.value;
    const rc = this.availableRCCompanies.find(r => r.id === rcId);
    return rc?.name || '';
  }

  getSelectedMCName(): string {
    const mcId = this.contactForm.get('mc_company_id')?.value;
    const mc = this.availableMCCompanies.find(m => m.id === mcId);
    return mc?.name || '';
  }

  loadCompanyDropdowns(): void {
    if (this.isRCUser()) {
      // Load MC companies for RC user
      this.rcOversightService.getMCCompaniesDropdown().subscribe({
        next: (companies) => {
          this.availableMCCompanies = companies;
        },
        error: (err) => {
          console.error('Error loading MC companies for RC:', err);
        }
      });
    } else if (this.isGodUser()) {
      // Load RC companies for God user
      this.godService.getRCCompaniesDropdown().subscribe({
        next: (companies) => {
          this.availableRCCompanies = companies;
        },
        error: (err) => {
          console.error('Error loading RC companies for God:', err);
        }
      });
    }
  }

  // Validate mobile input and show error if invalid characters are entered
  validateMobileInput(event: any): void {
    const value = event.target.value;
    const mobilePattern = /^[+]?[0-9\s\-\(\)]*$/;
    
    const mobileControl = this.contactForm.get('mobile');
    if (mobileControl) {
      if (value && !mobilePattern.test(value)) {
        mobileControl.setErrors({ pattern: true });
      } else if (mobileControl.errors?.['pattern']) {
        // Remove pattern error if input is now valid
        const errors = { ...mobileControl.errors };
        delete errors['pattern'];
        const hasOtherErrors = Object.keys(errors).length > 0;
        mobileControl.setErrors(hasOtherErrors ? errors : null);
      }
    }
  }

  // CV Dropdown methods
  toggleCVDropdown(contact: Contact): void {
    if (this.selectedDropdownContact === contact.id) {
      this.selectedDropdownContact = null;
    } else {
      this.selectedDropdownContact = contact.id || null;
    }
  }

  async viewCV(contact: Contact): Promise<void> {
    this.selectedDropdownContact = null;
    if (!contact.cv_file_name) {
      this.errorService.showError('No CV found for this contact');
      return;
    }
    
    try {
      // Use same method as CV upload component - open in new tab
      const response = await this.http.get(
        `${config.apiUrl}/api/contacts/${contact.id}/cv/view`,
        { 
          observe: 'response',
          responseType: 'blob'
        }
      ).toPromise();
      
      const contentType = response?.headers.get('Content-Type');
      
      if (contentType?.includes('application/json')) {
        // Parse JSON response (error or document message)
        const text = await response!.body!.text();
        const jsonResponse = JSON.parse(text);
        
        if (jsonResponse.data?.fileType === 'document') {
          this.errorService.showError(jsonResponse.data.message || 'This document type must be downloaded to view.');
          return;
        }
        
        if (!jsonResponse.success) {
          throw new Error(jsonResponse.message || 'Failed to view CV');
        }
      } else if (contentType?.includes('application/pdf')) {
        // PDF blob - open in new tab (same as CV upload component)
        const pdfBlob = response!.body!;
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
        
        // Clean up blob URL after a delay
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
        return;
      }
      
      // Fallback: open URL directly
      const viewUrl = `${config.apiUrl}/api/contacts/${contact.id}/cv/view`;
      window.open(viewUrl, '_blank');
      
    } catch (error: any) {
      console.error('Error viewing CV:', error);
      this.errorService.showError('Failed to view CV. You can try downloading instead.');
    }
  }
  
  closeCVViewer(): void {
    this.showCVViewer = false;
    this.selectedContactForCV = null;
  }

  downloadCV(contact: Contact): void {
    this.selectedDropdownContact = null;
    if (!contact.id) return;

    this.http.get(
      `${config.apiUrl}/api/contacts/${contact.id}/cv`,
      { responseType: 'blob' }
    ).subscribe({
      next: (response) => {
        const blob = new Blob([response], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = contact.cv_file_name || 'cv.pdf';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading CV:', error);
        this.errorService.showError('Failed to download CV');
      }
    });
  }

  deleteCV(contact: Contact): void {
    this.selectedDropdownContact = null;
    this.contactForCVDeletion = contact;
    this.showDeleteCVModal = true;
  }

  cancelDeleteCV(): void {
    this.showDeleteCVModal = false;
    this.contactForCVDeletion = null;
  }

  confirmDeleteCV(): void {
    if (!this.contactForCVDeletion?.id) return;

    this.http.delete(`${config.apiUrl}/api/contacts/${this.contactForCVDeletion.id}/cv`).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.errorService.showInfo('CV deleted successfully', true, 3000);
          this.loadContacts(); // Refresh contacts to update UI
        } else {
          this.errorService.showError(response.message || 'Failed to delete CV');
        }
        this.showDeleteCVModal = false;
        this.contactForCVDeletion = null;
      },
      error: (error) => {
        console.error('Error deleting CV:', error);
        this.errorService.showError('Failed to delete CV');
        this.showDeleteCVModal = false;
        this.contactForCVDeletion = null;
      }
    });
  }


  replaceCV(contact: Contact): void {
    this.selectedDropdownContact = null;
    this.contactForCVReplacement = contact;
    this.cvFileInput.nativeElement.click();
  }

  onReplacementCVFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    
    if (!file || !this.contactForCVReplacement) return;

    // Validate file
    const validation = this.validateCVFile(file);
    if (validation) {
      this.errorService.showError(validation);
      return;
    }

    // Upload the new CV
    this.uploadReplacementCV(file);
  }

  private validateCVFile(file: File): string | null {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'application/msword', 
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                         'text/plain'];
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt'];
    
    if (file.size > maxSize) {
      return 'File size must be less than 10MB';
    }
    
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(extension)) {
      return 'Only PDF, DOC, DOCX, and TXT files are allowed';
    }
    
    if (!allowedTypes.includes(file.type) && file.type !== '') {
      return 'Invalid file type';
    }
    
    return null;
  }

  private uploadReplacementCV(file: File): void {
    if (!this.contactForCVReplacement?.id) return;

    const formData = new FormData();
    formData.append('cv', file);

    this.http.post<any>(
      `${config.apiUrl}/api/contacts/${this.contactForCVReplacement.id}/cv`,
      formData
    ).subscribe({
      next: (response) => {
        if (response.success) {
          this.errorService.showInfo('CV replaced successfully', true, 3000);
          this.loadContacts(); // Refresh contacts to update UI
        } else {
          this.errorService.showError(response.message || 'Failed to replace CV');
        }
        this.contactForCVReplacement = null;
      },
      error: (error) => {
        console.error('Error replacing CV:', error);
        this.errorService.showError('Failed to replace CV');
        this.contactForCVReplacement = null;
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.cv-dropdown')) {
      this.selectedDropdownContact = null;
    }
  }


}